import { experimentSettings, xzwTypes, yTypes } from "./types.js";
import { FlagParameters, longToDate } from "./utility/headerUtils.js";
/**
 * Old-format File-header parsing.
 * @param buffer - spc buffer.
 * @param  prev - `{parameters,fileversion}`
 * @returns  file metadata
 */
export class TheOldHeader {
    fileVersion;
    parameters;
    exponentY;
    numberPoints;
    startingX;
    endingX;
    xUnitsType;
    yUnitsType;
    date;
    resolutionDescription;
    peakPointNumber;
    scans;
    spare;
    memo;
    xyzLabels;
    constructor(buffer, prev) {
        this.fileVersion = prev.fileVersion; //Each bit contains a parameter
        this.parameters = prev.parameters; //4B => New format; 4D => LabCalc format
        this.exponentY = buffer.readInt16(); //Word (16 bits) instead of byte
        this.numberPoints = buffer.readFloat32();
        this.startingX = buffer.readFloat32();
        this.endingX = buffer.readFloat32();
        this.xUnitsType = xzwTypes(buffer.readUint8());
        this.yUnitsType = yTypes(buffer.readUint8());
        const date = new Date();
        const zTypeYear = buffer.readUint16(); //Unrelated to Z axis
        date.setUTCFullYear(zTypeYear % 4096); // TODO: might be wrong
        date.setUTCMonth(Math.max(buffer.readUint8() - 1, 0));
        date.setUTCDate(buffer.readUint8());
        date.setUTCHours(buffer.readUint8());
        date.setUTCMinutes(buffer.readUint8());
        this.date = date.toISOString();
        this.resolutionDescription = buffer
            .readChars(8)
            .replaceAll('\u0000', '')
            .trim();
        this.peakPointNumber = buffer.readUint16();
        this.scans = buffer.readUint16();
        this.spare = [];
        for (let i = 0; i < 7; i++) {
            this.spare.push(buffer.readFloat32());
        }
        this.memo = buffer.readChars(130).replaceAll('\u0000', '').trim();
        this.xyzLabels = buffer.readChars(30).replaceAll('\u0000', '').trim();
    }
}
/**
 * New format file-header parsing.
 * @param buffer - spc buffer.
 * @param  prev - `{parameters,fileversion}`
 * @returns  file metadata
 */
export class TheNewHeader {
    fileVersion;
    parameters;
    experimentType;
    exponentY;
    numberPoints;
    startingX;
    endingX;
    spectra;
    xUnitsType;
    yUnitsType;
    zUnitsType;
    postingDisposition;
    date;
    resolutionDescription;
    sourceInstrumentDescription;
    peakPointNumber;
    spare;
    memo;
    xyzLabels;
    logOffset;
    modifiedFlag;
    processingCode;
    calibrationLevel;
    subMethodSampleInjectionNumber;
    concentrationFactor;
    methodFile;
    zSubIncrement;
    wPlanes;
    wPlaneIncrement;
    wAxisUnits;
    reserved;
    constructor(buffer, prev) {
        this.fileVersion = prev.fileVersion; //Each bit contains a parameter
        this.parameters = prev.parameters; //4B => New format; 4D => LabCalc format
        this.experimentType = experimentSettings(buffer.readUint8()); //Experiment type code (See SPC.h)
        this.exponentY = buffer.readInt8(); //Exponent for Y values (80h = floating point): FloatY = (2^Exp)*IntY/(2^32) 32-bit; FloatY = (2^Exp)*IntY/(2^16) 32-bit
        this.numberPoints = buffer.readUint32(); //Number of points (if not XYXY)
        this.startingX = buffer.readFloat64(); //First X coordinate
        this.endingX = buffer.readFloat64(); //Last X coordinate
        this.spectra = buffer.readUint32(); //Number of spectrums
        this.xUnitsType = xzwTypes(buffer.readUint8()); //X Units type code (See types.js)
        this.yUnitsType = yTypes(buffer.readUint8()); //Y ""
        this.zUnitsType = xzwTypes(buffer.readUint8()); //Z ""
        this.postingDisposition = buffer.readUint8(); //Posting disposition (See GRAMSDDE.H)
        this.date = longToDate(buffer.readUint32()); //Date: minutes = first 6 bits, hours = 5 next bits, days = 5 next, months = 4 next, years = 12 last
        this.resolutionDescription = buffer
            .readChars(9)
            .replaceAll('\u0000', '')
            .trim(); //Resolution description text
        this.sourceInstrumentDescription = buffer
            .readChars(9)
            .replaceAll('\u0000', '')
            .trim(); // Source Instrument description text
        this.peakPointNumber = buffer.readUint16(); //Peak point number for interferograms
        this.spare = [];
        for (let i = 0; i < 8; i++) {
            this.spare.push(buffer.readFloat32());
        }
        if (this.fileVersion === 0x4c) {
            //Untested case because no test files
            this.spare = this.spare.toReversed();
        }
        this.memo = buffer.readChars(130).replaceAll('\u0000', '').trim();
        this.xyzLabels = buffer.readChars(30).replaceAll('\u0000', '').trim();
        this.logOffset = buffer.readUint32(); //Byte offset to Log Block
        this.modifiedFlag = buffer.readUint32(); //File modification flag (See values in SPC.H)
        this.processingCode = buffer.readUint8(); //Processing code (See GRAMSDDE.H)
        this.calibrationLevel = buffer.readUint8(); //Calibration level + 1
        this.subMethodSampleInjectionNumber = buffer.readUint16(); //Sub-method sample injection number
        this.concentrationFactor = buffer.readFloat32(); //Floating data multiplier concentration factor
        this.methodFile = buffer.readChars(48).replaceAll('\u0000', '').trim(); //Method file
        this.zSubIncrement = buffer.readFloat32(); //Z subfile increment for even Z Multifiles
        this.wPlanes = buffer.readUint32();
        this.wPlaneIncrement = buffer.readFloat32();
        this.wAxisUnits = xzwTypes(buffer.readUint8()); //W axis units code
        this.reserved = buffer.readChars(187).replaceAll('\u0000', '').trim(); //Reserved space (Must be zero)
        if (this.xUnitsType === 0) {
            this.xUnitsType = this.xyzLabels.slice(0, 10);
        }
        if (this.zUnitsType === 0) {
            this.zUnitsType = this.xyzLabels.slice(20, 30);
        }
    }
}
/**
 * File-header parsing - First 512/256 bytes (new/old format).
 * @param buffer - SPC buffer.
 * @returns File-header object
 */
export function fileHeader(buffer) {
    const parameters = new FlagParameters(buffer.readUint8()); //Each bit contains a parameter
    const fileVersion = buffer.readUint8(); //4B => New format; 4D => LabCalc format
    const headerOpts = { parameters, fileVersion };
    switch (fileVersion) {
        case 0x4b: // new format
            break;
        case 0x4c:
            buffer.setBigEndian();
            break;
        case 0x4d: {
            // old LabCalc format
            return new TheOldHeader(buffer, headerOpts);
        }
        default:
            throw new Error('Unrecognized file format: byte 01 must be either 4B, 4C or 4D');
    }
    return new TheNewHeader(buffer, headerOpts);
}
//# sourceMappingURL=fileHeader.js.map